import {Card} from '@albert-io/atomic';
import {useDroppable} from '@dnd-kit/core';
import {useShallow} from 'zustand/react/shallow';
import {QuestionModelV3} from '@albert-io/models';
import React, {useEffect} from 'react';
import {MovableItem} from './BankItems.react';

import useQuestionAndDrafts from 'client/components/QuestionTypes/V2/useQuestionAndDrafts.hook';
import {WORD_BANK_ID} from '../store';
import classNames from 'classnames';

interface Props {
  question: QuestionModelV3;
  handleItemClick: (itemId: string) => void;
  handleBankClick?: () => void;
}

const WordBank = ({question, handleItemClick, handleBankClick}: Props) => {
  const {useStoreHook} = useQuestionAndDrafts(question);
  const {isOver, setNodeRef, node} = useDroppable({id: WORD_BANK_ID});

  const {wordBankItems, activeItemId, isDragging, isSubmitted} = useStoreHook(
    useShallow((state) => ({
      wordBankItems: state.wordBank,
      activeItemId: state.activeItemId,
      isDragging: state.isDragging,
      isSubmitted: state.isSubmitted
    }))
  );

  const handleClick = (e: React.MouseEvent) => {
    // Stop propagation to prevent conflicts with item clicks
    e.stopPropagation();
    handleBankClick?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleBankClick?.();
    }
  };

  useEffect(() => {
    const handleClickInside = (e: MouseEvent) => {
      // only handle clicks if an item is active
      if (activeItemId === null) return;
      const target = e.target as Element;
      const isChip = target.classList.contains('a-chip') || false;
      if (!isChip && node.current && node.current.contains(target)) {
        handleBankClick?.();
      }
    };
    document.addEventListener('click', handleClickInside);
    return () => {
      document.removeEventListener('click', handleClickInside);
    };
  }, [handleBankClick, node, activeItemId]);

  const isActiveTarget = activeItemId !== null && !isDragging;
  return (
    <Card
      ref={setNodeRef}
      paddingLevel={1}
      className={classNames('align-start flex flex-wrap gap-x1', {
        'border-selected background-subtle': isOver || isActiveTarget
      })}
    >
      {wordBankItems.map((item) => {
        const isInWordBank = item.placedInSlot === null;
        return isInWordBank ? (
          <MovableItem
            key={item.id}
            item={item}
            isActive={activeItemId === item.id}
            isDragging={isDragging}
            onClick={() => handleItemClick(item.id)}
            isSubmitted={isSubmitted}
            activeItemId={activeItemId}
          />
        ) : (
          <div
            key={item.id}
            className='p-2 min-h-x4 min-w-x9 rounded background-bold'
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            role='button'
            tabIndex={0}
          />
        );
      })}
    </Card>
  );
};

export default WordBank;
//
